import { DoseDetailView } from '~/domain/projections/views';
import { DoseDisplaySettings } from '~/state/atoms/dose/doseDisplaySettings';

/**
 * The display-enriched dose view consumed by the dose overlay, header and
 * properties panel. It is the domain {@link DoseDetailView}
 * (clinical fields) combined with the UI-layer per-dose display settings.
 *
 * The scaled and the unscaled maximum are **separate fields**, not one field
 * whose meaning depends on which view you happen to hold: the inherited
 * `maxDoseGy` stays the unscaled clinical maximum and
 * {@link DoseDisplayView.displayMaxDoseGy} carries the scaled value. A single
 * `maxDoseGy` with two meanings would be structurally assignable in both
 * directions, so a refactor could scale an already-scaled view with no type
 * error and no test failure — a dose readout wrong by the rescale factor is
 * exactly the kind of unverifiable change this codebase must not allow.
 */
export interface DoseDisplayView extends DoseDetailView {
    /** Dimensionless display multiplier currently applied. */
    readonly doseScale: number;
    /**
     * Effective 100 %-normalization dose in Gy — the display setting, or the
     * (unscaled) dose maximum when none has been chosen.
     */
    readonly normalizationValueGy: number;
    /**
     * Maximum dose in Gy with the display scale applied. Render this where
     * the user sees a rescaled dose; use the inherited `maxDoseGy` for the
     * unscaled clinical value.
     */
    readonly displayMaxDoseGy: number;
}

/**
 * Merge the clinical projection with the display settings. Pure, and
 * idempotent: because the scale is always applied to the unscaled
 * `maxDoseGy`, re-building from an already-built view cannot double-scale.
 */
export function buildDoseDisplayView(
    detail: DoseDetailView,
    settings: DoseDisplaySettings
): DoseDisplayView {
    const doseScale = settings.doseScale;
    return {
        ...detail,
        doseScale,
        // Default normalization is the UNSCALED maximum, matching the prior
        // behaviour of `DoseMatrix.normalizationValueGy`.
        normalizationValueGy: settings.normalizationValueGy ?? detail.maxDoseGy,
        displayMaxDoseGy: detail.maxDoseGy * doseScale,
    };
}
